import { useCallback, useEffect, useRef, useState } from "react";
import Message from "./Message";
import { ButtonGoFinalChat } from "../../../components/ButtonGoFinalChat";
import { useChatStore } from "../../../state/chat";
import { IMessage } from "../../../@DTO/chat";

interface ConversationProps {
  chatUserConversations: IMessage[];
}

const Conversation = ({ chatUserConversations }: ConversationProps) => {
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const conversationRef = useRef<HTMLDivElement>(null);
  const { isReceivingMessage } = useChatStore();

  const [tempAiMessage, setTempAiMessage] = useState<IMessage | null>(null);

  // Create a temporary AI message while it is being received
  useEffect(() => {
    if (isReceivingMessage) {
      setTempAiMessage({
        type: "ai",
        data: {
          content: "",
          tool_calls: [],
          invalid_tool_calls: [],
          additional_kwargs: {},
          response_metadata: {},
        },
      });
    } else {
      setTempAiMessage(null);
    }
  }, [isReceivingMessage]);

  // Scrolls to bottom when the "Go to end" button is clicked
  const handleGoBackFinalChat = useCallback(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTo({
        top: conversationRef.current.scrollHeight,
        behavior: "smooth",
      });
      setUserScrolledUp(false);
      setShouldScrollToBottom(true);
    }
  }, []);

  // Detects when user scrolls and disables auto-scroll if scrolling up
  const handleScroll = useCallback(() => {
    if (conversationRef.current) {
      const element = conversationRef.current;
      const isAtBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight < 50;

      if (!isAtBottom && !userScrolledUp) {
        setUserScrolledUp(true); // Disable auto-scroll if user scrolls up
        setShouldScrollToBottom(false);
      }

      setShowButton(!isAtBottom); // Show "Go to end" button if not at bottom
    }
  }, [userScrolledUp]);

  // Automatically scroll to bottom if allowed
  useEffect(() => {
    if (!userScrolledUp && shouldScrollToBottom && conversationRef.current) {
      requestAnimationFrame(() => {
        conversationRef.current?.scrollTo({
          top: conversationRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    }
  }, [chatUserConversations, tempAiMessage, shouldScrollToBottom, userScrolledUp]);

  useEffect(() => {
    if (
      chatUserConversations.length > 0 &&
      chatUserConversations[chatUserConversations.length - 1].type === "human"
    ) {
      setShouldScrollToBottom(true);
      setUserScrolledUp(false);
    }
  }, [chatUserConversations]);

  return (
    <>
      <div
        className="chat-conversation d-flex flex-column me-sm-2 h-100 overflow-auto"
        ref={conversationRef}
        onScroll={handleScroll}
      >
        <div className="list-unstyled chat-conversation-list">
          {chatUserConversations?.map((message, index) => {
            const isLastMessage =
              index === chatUserConversations.length - 1 && !tempAiMessage;
            return (
              <Message
                message={message}
                key={index}
                isLastMessage={isLastMessage}
              />
            );
          })}

          {tempAiMessage && (
            <Message message={tempAiMessage} isLastMessage={true} />
          )}
        </div>
      </div>

      {showButton && <ButtonGoFinalChat onClick={handleGoBackFinalChat} />}
    </>
  );
};

export default Conversation;
